import type { SiteBannerSegment } from '@viamrobotics/prime-core';

import { getDurablePref, setDurablePref } from '$lib/card-prefs';

const DISMISSAL_KEY = 'announcement-banner-dismissal';

export interface Announcement {
  /** Optional leading badge, e.g. "New". Omit to hide the badge. */
  badge?: string;
  /** The banner copy, as styled runs. */
  segments: SiteBannerSegment[];
  /** Campaign start time, unix seconds. The banner shows from this time onward. */
  startTime: number;
}

interface DismissalRecord {
  /** When the user dismissed the banner, unix seconds. */
  dismissedAt: number;
}

const nowSeconds = () => Math.floor(Date.now() / 1000);

const isValidSegment = (seg: unknown): seg is SiteBannerSegment => {
  if (typeof seg !== 'object' || seg === null) {
    return false;
  }
  const { text, style, href } = seg as Record<string, unknown>;
  return (
    typeof text === 'string' &&
    text.length > 0 &&
    (style === undefined || style === 'strong' || style === 'subtle') &&
    (href === undefined || (typeof href === 'string' && href.length > 0))
  );
};

/** Parse the ANNOUNCEMENT_BANNER flag value, returning null if it is missing or malformed. */
export const parseAnnouncement = (raw: string): Announcement | null => {
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (
      typeof parsed.startTime === 'number' &&
      Array.isArray(parsed.segments) &&
      parsed.segments.length > 0 &&
      parsed.segments.every(isValidSegment)
    ) {
      return {
        ...(typeof parsed.badge === 'string' && parsed.badge.length > 0
          ? { badge: parsed.badge }
          : {}),
        segments: parsed.segments,
        startTime: parsed.startTime,
      };
    }
  } catch {
    // Malformed flag value — treat as no announcement.
  }
  return null;
};

const readDismissal = (): DismissalRecord | null => {
  const raw = getDurablePref(DISMISSAL_KEY);
  if (raw === undefined) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as Partial<DismissalRecord>;
    if (typeof parsed.dismissedAt === 'number') {
      return { dismissedAt: parsed.dismissedAt };
    }
  } catch {
    // Corrupt record — treat as never dismissed.
  }
  return null;
};

/**
 * The banner shows once its campaign has started (startTime reached) and the
 * user has not dismissed it since this campaign began. A newer campaign (later
 * startTime than the last dismissal) re-shows after a prior dismissal.
 */
export const shouldShowAnnouncement = (announcement: Announcement): boolean => {
  if (announcement.startTime > nowSeconds()) {
    return false;
  }
  const record = readDismissal();
  return record === null || record.dismissedAt < announcement.startTime;
};

/** Record that the current announcement was dismissed, stamped with the current time. */
export const dismissAnnouncement = (): void => {
  setDurablePref(DISMISSAL_KEY, JSON.stringify({ dismissedAt: nowSeconds() }));
};
